import type { AxiosRequestConfig } from "axios";
import { ApiService } from "./api.service";
import { NOTIFICATIONS_BASE_URL } from "@/config";
import type {
  TNotification,
  TNotificationListResponse,
  TNotificationMarkAllPayload,
  TNotificationQuery,
  TNotificationUpdatePayload,
  TUnreadNotificationCount,
} from "@/types/notification";

function buildListParams(query: TNotificationQuery): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  if (query.type) params.type = query.type;
  if (query.snoozed) params.snoozed = true;
  if (query.archived) params.archived = true;
  if (query.mentioned) params.mentioned = true;
  if (query.read !== undefined) params.read = query.read;
  if (query.per_page !== undefined) params.per_page = query.per_page;
  if (query.cursor) params.cursor = query.cursor;
  return params;
}

function buildStreamUrl(workspaceSlug: string): string {
  const path = `/api/workspaces/${encodeURIComponent(workspaceSlug)}/users/notifications/stream/`;
  const base = NOTIFICATIONS_BASE_URL.replace(/\/$/, "");
  return base ? `${base}${path}` : path;
}

export class NotificationService extends ApiService {
  openStream(workspaceSlug: string): EventSource | null {
    if (typeof EventSource === "undefined") return null;
    return new EventSource(buildStreamUrl(workspaceSlug), { withCredentials: true });
  }

  // `config` lets the caller pass an AbortSignal so the store can cancel these
  // badge/preview polls when the tab hides (#2756).
  async getUnreadCount(
    workspaceSlug: string,
    config?: AxiosRequestConfig,
  ): Promise<TUnreadNotificationCount> {
    return this.get(`/api/workspaces/${workspaceSlug}/users/notifications/unread/`, config);
  }

  async list(
    workspaceSlug: string,
    query: TNotificationQuery = {},
    config?: AxiosRequestConfig,
  ): Promise<TNotificationListResponse> {
    return this.get(`/api/workspaces/${workspaceSlug}/users/notifications/`, {
      params: buildListParams(query),
      ...config,
    });
  }

  // Single notification by id. The list endpoint can't stand in for this: its
  // rows are scoped to a tab/filter and one page, while a deep link
  // (`?notificationId=`) can name anything the receiver owns.
  async retrieve(workspaceSlug: string, notificationId: string): Promise<TNotification> {
    return this.get(`/api/workspaces/${workspaceSlug}/users/notifications/${notificationId}/`);
  }

  async update(
    workspaceSlug: string,
    notificationId: string,
    payload: TNotificationUpdatePayload,
  ): Promise<TNotification> {
    return this.patch(
      `/api/workspaces/${workspaceSlug}/users/notifications/${notificationId}/`,
      payload,
    );
  }

  // The read/unread/archive/unarchive endpoints return 204 No Content — no
  // notification body — so these resolve to void and callers keep their
  // optimistic state rather than overwriting the row with an empty response.
  async markAsRead(workspaceSlug: string, notificationId: string): Promise<void> {
    await this.post(`/api/workspaces/${workspaceSlug}/users/notifications/${notificationId}/read/`);
  }

  async markAsUnread(workspaceSlug: string, notificationId: string): Promise<void> {
    await this.delete(
      `/api/workspaces/${workspaceSlug}/users/notifications/${notificationId}/unread/`,
    );
  }

  async archive(workspaceSlug: string, notificationId: string): Promise<void> {
    await this.post(
      `/api/workspaces/${workspaceSlug}/users/notifications/${notificationId}/archive/`,
    );
  }

  async unarchive(workspaceSlug: string, notificationId: string): Promise<void> {
    await this.delete(
      `/api/workspaces/${workspaceSlug}/users/notifications/${notificationId}/unarchive/`,
    );
  }

  async markAllAsRead(
    workspaceSlug: string,
    payload: TNotificationMarkAllPayload,
  ): Promise<{ message: string }> {
    return this.post(
      `/api/workspaces/${workspaceSlug}/users/notifications/mark-all-read/`,
      payload,
    );
  }
}
